"use client";
import { Box, Button, Typography } from "@mui/material";

const mintGradient = "linear-gradient(135deg, #123524 0%, #1f7a4d 100%)";
const mintGradientHover = "linear-gradient(135deg, #0f2d1f 0%, #1a6b42 100%)";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { AddToCartService, Cart } from "@/app/services/cart";
import { useCurrencyCode } from "../../helper-functions/CurrencySymbol";
import { Loader } from "../../MainLoader/Loader";

interface CartButtonProps {
  cid?: number;
}

export const CartButton = ({ cid: categoryId }: CartButtonProps) => {
  const router = useRouter();
  const addToCartService = useMemo(() => new AddToCartService(), []);
  const [cart, setCart] = useState(new Cart());
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const currencyCode = useCurrencyCode();

  const handleNaviagteToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    setLoading(true);
    router.push("/cart");
  };

  const getInitializeCart = useCallback(() => {
    const mainCart = addToCartService.getCart();
    if (!mainCart) {
      return;
    }
    const cartCidValid = mainCart?.cartItemRequestsById.find(
      (item: { cid: number }) => item.cid === categoryId
    );

    if (cartCidValid) {
      const totalCount = cartCidValid?.cartItemRequests.reduce(
        (acc: number, item: { count: number }) => acc + item.count,
        0
      );
      setCart(cartCidValid);
      setCount(totalCount);
    } else {
      setCart(new Cart());
      setCount(0);
    }
  }, [addToCartService, categoryId]);

  useEffect(() => {
    const intr = setInterval(() => {
      getInitializeCart();
    }, 1000);
    return () => {
      clearInterval(intr);
    };
  }, [getInitializeCart]);

  const disabled =
    cart?.cartItemRequests?.length === 0 ||
    cart == null ||
    Object.keys(cart).length === 0;

  return (
    <Box
      sx={{
        display: { xs: "block", sm: "none" },
        background: "#fff",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        px: 2,
        pt: 1,
        pb: (theme) =>
          `max(${theme.spacing(1)}, env(safe-area-inset-bottom, 0px))`,
        borderTop: "1px solid rgba(18,53,36,0.08)",
        boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
      }}
    >
      <Button
        variant="contained"
        onClick={handleNaviagteToCart}
        disabled={disabled}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%",
          minHeight: 48,
          m: 0,
          boxSizing: "border-box",
          borderRadius: "999px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: { xs: 1.5, sm: 2 },
          py: 1,
          textTransform: "none",
          fontWeight: 600,
          background: mintGradient,
          boxShadow: "0 8px 24px rgba(18, 53, 36, 0.22)",
          "&:hover": {
            background: mintGradientHover,
          },
          "&.Mui-disabled": {
            background: "rgba(18, 53, 36, 0.2)",
            color: "rgba(255,255,255,0.7)",
          },
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "inherit",
              background: "rgba(18,53,36,0.35)",
              zIndex: 1,
            }}
          >
            <Loader />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: "#123524",
              background: "rgba(255,255,255,0.95)",
              borderRadius: "100%",
              border: "2px solid rgba(255,255,255,0.5)",
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
              width: 27,
              height: 27,
              minWidth: 27,
              fontWeight: "bold",
              mr: 1,
              fontSize: "0.8rem",
            }}
          >
            {count ? count : 0}
          </Typography>
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              minWidth: 0,
              flex: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                textAlign: "left",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {currencyCode}
              {cart?.totalAmount ? cart?.totalAmount : 0}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.5,
                rowGap: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  textDecoration: "line-through",
                  fontSize: "0.65rem",
                  lineHeight: 1.2,
                  opacity: 0.95,
                }}
              >
                {currencyCode}
                {cart?.totalMrp ? cart?.totalMrp : 0}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  textTransform: "capitalize",
                  fontSize: "0.65rem",
                  lineHeight: 1.2,
                }}
              >
                Save {currencyCode}
                {cart?.totalDiscount ? cart?.totalDiscount : 0}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            pl: 0.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              display: "flex",
              alignItems: "center",
              fontWeight: "bold",
              whiteSpace: "nowrap",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            Continue <KeyboardArrowRightIcon sx={{ fontSize: "1.1rem" }} />
          </Typography>
        </Box>
      </Button>
    </Box>
  );
};
